import React, { Fragment, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Typography,
  Avatar,
  useTheme,
  Chip,
  LinearProgress,
  Switch,
} from "@mui/material";
import {
  Autorenew,
  CheckCircleOutline,
  CheckCircleRounded,
  RadioButtonCheckedRounded,
  RadioButtonUncheckedRounded,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { usePostHog } from "posthog-js/react";
import { TOGGLE_DEBUG_MODE, CHANGE_MODEL } from "../../../utils/posthogEvents";
import { makeStyles } from "tss-react/mui";
import {
  getAIModel,
  getIsDebugModeOn,
  getUserDetails,
  getAccountDetails,
  setAIModel,
  setIsDebugModeOn,
  setUser,
} from "../../../core/storage/localStorage";
import { upgradeRequest } from "../../../core/repo/accountRepo";
import {
  getUserNameShortform,
  toTitleCase,
  models,
} from "../../../utils/utils";
import EditUsername from "./EditUsername";
import Page from "../../../components/Page";
import Header from "./Header";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
  },
  bar: {
    background: "#D7D7D7",
  },
  bar1Determinate: {
    background: theme.palette.success.main,
    borderRadius: 4,
  },
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
}));

function Account() {
  const posthog = usePostHog();
  const user = getUserDetails();
  const account = getAccountDetails();
  const { classes } = useStyles();
  const { name, email, num_of_messages, upgrade_request } = user;
  const {
    name: accountName,
    plan,
    cover_image,
    profile_image,
    is_org,
  } = account;
  const isPro = plan !== "free";
  const [selectedModel, setSelectedModel] = useState(() => getAIModel());
  const [isDebugMode, setIsDebugMode] = useState(() => getIsDebugModeOn());
  const [showUpgradeRequest, setShowUpgradeRequest] = useState(
    !upgrade_request
  );
  const [isUpgradeRequestLoading, setIsUpgradeRequestLoading] = useState(false);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const handleUpgradeClick = async () => {
    setIsUpgradeRequestLoading(true);
    try {
      await dispatch(upgradeRequest());
      setOpen(true);
      setTimeout(() => {
        setUser({ ...user, isBannerOpen: false, upgrade_request: true });
        window.dispatchEvent(new Event("storage"));
        setOpen(false);
        setShowUpgradeRequest(false);
      }, 5000);
    } catch (err) {
      console.log(err);
    }
    setIsUpgradeRequestLoading(false);
  };

  const handleModelChange = (model) => {
    setAIModel(model);
    setSelectedModel(model);
    window.dispatchEvent(new Event("storage"));
    posthog.capture(CHANGE_MODEL, { model });
  };

  const handleDebugMode = (e) => {
    const value = e.target.checked;
    setIsDebugModeOn(value);
    setIsDebugMode(value);
    window.dispatchEvent(new Event("storage"));
    posthog.capture(TOGGLE_DEBUG_MODE, { value });
  };

  return (
    <Page className={classes.root} title={"Account Settings"}>
      <Box
        height={"100%"}
        display={"flex"}
        flexDirection={"column"}
        overflow={"hidden"}>
        <Header />
        <Box flex={1} overflow={"auto"}>
          <Box p={{ xs: 2, sm: 3 }} maxWidth={900} margin={"0 auto"}>
            <Box position={"relative"} mb={4}>
              <Box
                sx={{
                  width: "100%",
                  height: 77,
                  backgroundColor: cover_image ? "transparent" : "grey",
                }}>
                <img
                  src={cover_image ? cover_image : "/images/dialog_title.png"}
                  width={"100%"}
                  height={77}
                  style={{ display: "block", objectFit: "cover" }}
                />
              </Box>
              <Avatar
                src={profile_image}
                imgProps={{ style: { objectFit: "contain" } }}
                sx={{
                  width: 56,
                  height: 56,
                  border: "2px solid white",
                  position: "absolute",
                  left: "50%",
                  bottom: 0,
                  transform: "translate(-50%,50%)",
                  background: isPro
                    ? "linear-gradient(139deg, #BCDBD5 14.33%, #867FDF 35.09%, #370084 68.69%)"
                    : "#263238",
                  textTransform: "capitalize",
                }}>
                {getUserNameShortform(is_org ? accountName : name)}
              </Avatar>
            </Box>
            <Box mt={4}>
              {is_org ? (
                <Typography variant="h5" textAlign={"center"}>
                  <span className={isPro ? classes.gradientText : null}>
                    {accountName}
                  </span>
                </Typography>
              ) : (
                <EditUsername user={user} isCenter={true}>
                  <Typography variant="h5" textAlign={"center"} sx={{ ml: 4 }}>
                    <span className={isPro ? classes.gradientText : null}>
                      {name}
                    </span>
                  </Typography>
                </EditUsername>
              )}
              {!is_org && (
                <Typography
                  variant="body2"
                  color={"textSecondary"}
                  textAlign={"center"}>
                  {email}
                </Typography>
              )}
            </Box>
            <Box px={3} py={2.5}>
              <Box
                borderRadius={3}
                bgcolor={theme.palette.background.light}
                p={1.5}>
                {is_org && (
                  <>
                    <Box display={"flex"} alignItems={"center"}>
                      <Avatar
                        sx={{
                          background: isPro
                            ? "linear-gradient(139deg, #BCDBD5 14.33%, #867FDF 35.09%, #370084 68.69%)"
                            : "#263238",
                          textTransform: "capitalize",
                        }}>
                        {getUserNameShortform(name)}
                      </Avatar>
                      <Box mr={1} />
                      <Box width={"100%"}>
                        <EditUsername user={user}>
                          <Typography fontWeight={500}>{name}</Typography>
                        </EditUsername>
                        <Typography variant="body2" color={"textSecondary"}>
                          {email}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                  </>
                )}
                {open ? (
                  <Chip
                    label="We’ll reach out soon with upgrade steps & payment."
                    icon={
                      <CheckCircleOutline
                        fontSize="small"
                        sx={{ color: "white !important" }}
                      />
                    }
                    sx={{
                      width: "100%",
                      background: theme.palette.success.main,
                      color: "white",
                      justifyContent: "start",
                    }}
                  />
                ) : (
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}>
                    <Typography>
                      Plan:
                      <Box display={"inline-block"} mr={1.5} />
                      <b>
                        <span className={isPro ? classes.gradientText : null}>
                          {toTitleCase(plan)}
                        </span>
                      </b>
                    </Typography>
                    {plan === "free" && !is_org && (
                      <Chip
                        icon={
                          isUpgradeRequestLoading ? (
                            <Autorenew
                              fontSize="small"
                              sx={{ color: "white !important" }}
                            />
                          ) : null
                        }
                        onClick={
                          isUpgradeRequestLoading || !showUpgradeRequest
                            ? null
                            : handleUpgradeClick
                        }
                        variant="filled"
                        label={
                          isUpgradeRequestLoading
                            ? "Please wait..."
                            : showUpgradeRequest
                            ? "🚀 Upgrade"
                            : "Upgrade Requested"
                        }
                        sx={{
                          background: showUpgradeRequest
                            ? theme.palette.light
                            : "#757575",
                          color: "white",
                        }}
                      />
                    )}
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Box
                    mb={1}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}>
                    <Typography>Query Usage</Typography>
                    <Typography variant="body2" color={"textSecondary"}>
                      Resets every month
                    </Typography>
                  </Box>
                  <Box width={"100%"} display={"flex"} alignItems={"center"}>
                    <LinearProgress
                      classes={{
                        root: classes.bar,
                        bar1Determinate: classes.bar1Determinate,
                      }}
                      variant="determinate"
                      value={(num_of_messages || 0) * 2}
                      sx={{ width: "100%", borderRadius: 1, height: 8 }}
                    />
                    <Box mr={3} />
                    <Typography variant="body2">
                      {num_of_messages || 0}/50
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {process.env.REACT_APP_ENV === "staging" && (
                <Box
                  p={1}
                  mt={2.5}
                  bgcolor={theme.palette.background.light}
                  borderRadius={3}>
                  <Box display={"flex"} alignItems={"center"}>
                    <Typography>Debug Mode</Typography>
                    <Box mr={1} />
                    <Switch checked={isDebugMode} onChange={handleDebugMode} />
                  </Box>
                  {isDebugMode && (
                    <Box>
                      {models.map((model, i) => {
                        const isSelected = selectedModel === model;
                        return (
                          <Fragment key={model}>
                            <Box
                              width={"100%"}
                              display={"flex"}
                              alignItems={"center"}
                              justifyContent={"space-between"}
                              sx={{ cursor: "pointer" }}
                              onClick={() => handleModelChange(model)}>
                              <Typography>{model}</Typography>
                              <IconButton>
                                {isSelected ? (
                                  <RadioButtonCheckedRounded />
                                ) : (
                                  <RadioButtonUncheckedRounded />
                                )}
                              </IconButton>
                            </Box>
                            {i !== models.length - 1 && (
                              <Divider sx={{ marginY: 0.5 }} />
                            )}
                          </Fragment>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
              {plan === "free" && (
                <Box
                  mt={2.5}
                  px={1.5}
                  py={1}
                  borderRadius={2}
                  sx={{
                    background:
                      "linear-gradient(280deg, #E3DFFC 0.82%, #FEFEFF 50.21%, #FCF2FD 99.6%)",
                  }}>
                  <Typography fontWeight={600}>
                    <span className={classes.gradientText}>
                      Why you need the PRO plan?
                    </span>
                  </Typography>
                  <Box display={"flex"} alignItems={"center"} py={1}>
                    <CheckCircleRounded color="success" />
                    <Box mr={1} />
                    <Typography variant="body2">No limit on queries</Typography>
                  </Box>
                  <Box display={"flex"} alignItems={"center"} py={0.5}>
                    <CheckCircleRounded color="success" />
                    <Box mr={1} />
                    <Typography variant="body2">
                      Diagram/Image search
                    </Typography>
                  </Box>
                  <Box display={"flex"} alignItems={"center"} py={0.5}>
                    <CheckCircleRounded color="success" />
                    <Box mr={1} />
                    <Typography variant="body2">API access</Typography>
                  </Box>
                  <Box display={"flex"} alignItems={"center"} py={0.5}>
                    <CheckCircleRounded color="success" />
                    <Box mr={1} />
                    <Typography variant="body2">Integrations</Typography>
                  </Box>
                  <Box display={"flex"} alignItems={"center"} py={0.5}>
                    <CheckCircleRounded color="success" />
                    <Box mr={1} />
                    <Typography variant="body2">
                      All capabilities of Free plan
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Page>
  );
}

export default Account;
